class PredictiveSearch extends HTMLElement {
  static cache = new Map();
  static nextId = 0;

  connectedCallback() {
    if (this.events) return;
    this.input = this.querySelector('input[name="q"]');
    this.panel = this.querySelector('[data-predictive-search]');
    this.startPanel = this.querySelector('[data-quick-trending-products]');
    this.status = this.querySelector('.predictive-search-status');
    if (!this.input || !this.panel) return;
    this.form = this.input.form;
    this.idPrefix = `dt-search-${++PredictiveSearch.nextId}`;
    this.events = new AbortController();
    this.sequence = 0;
    this.isOpen = false;
    this.input.setAttribute('aria-controls', `${this.idPrefix}-predictive-search-results-list`);
    this.input.removeAttribute('aria-owns');
    let typeInput = this.form.querySelector('input[name="type"]');
    if (!typeInput) {
      typeInput = document.createElement('input');
      typeInput.type = 'hidden';
      typeInput.name = 'type';
      this.form.append(typeInput);
    }
    typeInput.value = 'product';
    const options = { signal: this.events.signal };
    this.input.addEventListener('input', () => this.onInput(), options);
    this.input.addEventListener('focus', () => this.onFocus(), options);
    this.input.addEventListener('keydown', event => this.onKeydown(event), options);
    this.input.addEventListener('compositionstart', () => { this.composing = true; this.cancel(); }, options);
    this.input.addEventListener('compositionend', () => { this.composing = false; this.onInput(); }, options);
    this.form.addEventListener('submit', event => {
      if (!this.input.value.trim()) event.preventDefault();
    }, options);
    this.form.addEventListener('reset', () => {
      requestAnimationFrame(() => { this.input.value = ''; this.onInput(); this.input.focus(); });
    }, options);
    this.addEventListener('focusout', () => {
      clearTimeout(this.focusTimer);
      this.focusTimer = setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      }, 0);
    }, options);
    this.addEventListener('click', event => {
      if (event.target.closest('.header-search-close, .header-search-popup-close')) this.close();
    }, options);
    this.outsideClick = event => { if (!this.contains(event.target)) this.close(); };
    this.resize = () => { if (this.isOpen) this.sizePanel(); };
    window.addEventListener('resize', this.resize, options);
    window.visualViewport?.addEventListener('resize', this.resize, options);
    this.drawer = this.closest('.halo-sidebar_search');
    if (this.drawer) {
      document.addEventListener('click', event => {
        const opener = event.target.closest('[data-search-mobile]');
        if (!opener) return;
        this.opener = opener;
        requestAnimationFrame(() => this.input.focus());
      }, options);
      this.drawer.querySelector('[data-search-close-sidebar]')?.addEventListener('click', () => {
        this.close();
        this.opener?.focus();
      }, options);
      this.drawer.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.drawer.querySelector('[data-search-close-sidebar]')?.click();
        }
        if (event.key !== 'Tab') return;
        const items = [...this.drawer.querySelectorAll('a[href]:not([tabindex="-1"]),button:not([disabled]),input:not([type="hidden"]):not([disabled])')].filter(item => item.offsetParent !== null);
        const target = event.shiftKey && document.activeElement === items[0] ? items.at(-1) : !event.shiftKey && document.activeElement === items.at(-1) ? items[0] : null;
        if (target) { event.preventDefault(); target.focus(); }
      }, options);
    }
  }

  disconnectedCallback() {
    this.cancel();
    clearTimeout(this.focusTimer);
    this.events?.abort();
    document.removeEventListener('pointerdown', this.outsideClick);
    this.events = null;
  }

  cancel() {
    clearTimeout(this.timer);
    this.request?.abort();
    this.sequence++;
    this.removeAttribute('loading');
    this.panel?.removeAttribute('aria-busy');
  }

  onInput() {
    this.cancel();
    this.clearSelection();
    this.status.textContent = '';
    if (this.composing) return;
    const term = this.input.value.trim();
    this.panel.hidden = true;
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    if (!term) { this.showStart(); return; }
    if (this.startPanel) this.startPanel.hidden = true;
    this.timer = setTimeout(() => this.getSearchResults(term), 240);
  }

  onFocus() {
    const term = this.input.value.trim();
    if (!term) this.showStart();
    else if (term === this.renderedTerm && this.panel.firstElementChild) this.open();
    else this.getSearchResults(term);
  }

  showStart() {
    this.panel.hidden = true;
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    if (this.startPanel) this.startPanel.hidden = false;
    this.status.textContent = '';
    this.isOpen = true;
    this.sizePanel();
    document.addEventListener('pointerdown', this.outsideClick);
  }

  async getSearchResults(term) {
    this.cancel();
    const sequence = this.sequence;
    this.request = new AbortController();
    const url = new URL(window.routes.predictive_search_url, document.baseURI);
    url.searchParams.set('q', term);
    url.searchParams.set('section_id', 'predictive-search');
    url.searchParams.set('resources[type]', 'product,collection,query');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    const currency = document.querySelector('[name="currency_code"]')?.value || window.Currency?.currentCurrency || window.shop_currency || '';
    const key = `${url.href}|${document.documentElement.lang}|${currency}`;
    if (PredictiveSearch.cache.has(key)) {
      this.render(PredictiveSearch.cache.get(key), term);
      return;
    }
    if (this.startPanel) this.startPanel.hidden = true;
    this.panel.replaceChildren();
    const loading = document.createElement('p');
    loading.className = 'dt-search-message';
    loading.textContent = this.dataset.loadingText || '';
    this.panel.append(loading);
    this.open();
    this.setAttribute('loading', '');
    this.panel.setAttribute('aria-busy', 'true');
    this.status.textContent = loading.textContent;
    try {
      const response = await fetch(url, { signal: this.request.signal });
      if (!response.ok) throw new Error(`Search ${response.status}`);
      const text = await response.text();
      if (sequence !== this.sequence || term !== this.input.value.trim()) return;
      const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
      if (!section) throw new Error('Search section missing');
      if (PredictiveSearch.cache.size >= 20) PredictiveSearch.cache.delete(PredictiveSearch.cache.keys().next().value);
      PredictiveSearch.cache.set(key, section.innerHTML);
      this.render(section.innerHTML, term);
    } catch (error) {
      if (error.name === 'AbortError' || sequence !== this.sequence) return;
      this.panel.replaceChildren();
      const message = document.createElement('p');
      message.className = 'dt-search-message';
      message.textContent = this.panel.dataset.errorText;
      const link = document.createElement('a');
      link.className = 'dt-search-all';
      link.textContent = this.panel.dataset.allText;
      const destination = new URL(this.form.action, document.baseURI);
      destination.searchParams.set('q', term);
      destination.searchParams.set('type', 'product');
      link.href = destination.href;
      this.panel.append(message, link);
      this.status.textContent = message.textContent;
      this.open();
    } finally {
      if (sequence === this.sequence) {
        this.removeAttribute('loading');
        this.panel.removeAttribute('aria-busy');
      }
    }
  }

  render(markup, term) {
    this.panel.innerHTML = markup;
    this.panel.querySelectorAll('[id]').forEach(element => { element.id = `${this.idPrefix}-${element.id}`; });
    this.panel.querySelectorAll('[aria-labelledby]').forEach(element => {
      element.setAttribute('aria-labelledby', element.getAttribute('aria-labelledby').split(' ').map(id => `${this.idPrefix}-${id}`).join(' '));
    });
    this.renderedTerm = term;
    this.clearSelection();
    this.open();
    this.status.textContent = this.panel.querySelector('[data-predictive-search-live-region-count-value]')?.textContent || '';
    if (window.Currency?.convertAll && window.Currency.currentCurrency && (window.show_multiple_currencies || window.show_auto_currency)) {
      window.Currency.convertAll(window.shop_currency, window.Currency.currentCurrency, 'span.money', 'money_format');
    }
  }

  open() {
    this.panel.hidden = false;
    if (this.startPanel) this.startPanel.hidden = true;
    this.setAttribute('open', '');
    this.setAttribute('results', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
    this.sizePanel();
    document.addEventListener('pointerdown', this.outsideClick);
  }

  sizePanel() {
    const viewport = window.visualViewport;
    const viewportBottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
    const available = Math.max(120, viewportBottom - this.input.getBoundingClientRect().bottom - 24);
    this.style.setProperty('--dt-search-height', `${Math.min(640, available)}px`);
  }

  close(clearSearchTerm = false) {
    this.cancel();
    this.panel.hidden = true;
    if (this.startPanel) this.startPanel.hidden = true;
    this.removeAttribute('open');
    this.isOpen = false;
    this.input.setAttribute('aria-expanded', 'false');
    this.clearSelection();
    this.status.textContent = '';
    if (clearSearchTerm) this.input.value = '';
    document.removeEventListener('pointerdown', this.outsideClick);
  }

  clearSelection() {
    this.panel.querySelector('[aria-selected="true"]')?.setAttribute('aria-selected', 'false');
    this.input.removeAttribute('aria-activedescendant');
  }

  onKeydown(event) {
    if (event.isComposing) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      this.closest('.halo-sidebar_search')?.querySelector('[data-search-close-sidebar]')?.click();
      return;
    }
    if (event.key === 'Enter') {
      const selected = this.panel.querySelector('[aria-selected="true"] a');
      if (!this.panel.hidden && selected) { event.preventDefault(); selected.click(); }
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const options = [...this.panel.querySelectorAll('[role="option"]')];
    if (this.panel.hidden || !options.length) return;
    event.preventDefault();
    const current = options.findIndex(option => option.getAttribute('aria-selected') === 'true');
    const index = event.key === 'ArrowDown' ? (current + 1) % options.length : (current < 0 ? options.length - 1 : (current - 1 + options.length) % options.length);
    this.clearSelection();
    options[index].setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', options[index].id);
    options[index].scrollIntoView({ block: 'nearest' });
  }
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
